import type { ReactElement } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import ProgressSpinner from "@/Shared/Icons/ProgressSpinner";
import { ProgressSpinnerSizes } from "@/Shared/Icons/ProgressSpinnerSizes";
import { ShopifyAdminApi } from "../../Apis/ShopifyAdminApi";
import { apiServerUrl } from "../../Environment";
import { useAuthentication } from "@/Slices/authenticationSlice";
import {
  isRetryable,
  merchantMessage,
  requestShopifyToken,
} from "./Helpers/shopifyConnectSession";
import {
  ShopifyConnectCard_,
  ConnectShopifySpinnerRow_,
} from "./Internal/ShopifyConnectCard_";
// Cross-feature import of the canonical sign-in form — reused rather than duplicating auth logic
// so the merchant can sign in inside the connect popup (ENG-6034).
import { AuthenticationSignIn } from "@/Shared/Authentication/CustomComponents/AuthenticationSignIn";

/**
 * The popup window opened by the Shopify iframe at /connect-shopify. Origin: dashboard.freightsimple.com.
 *
 * Flow:
 *   1. Read `?iframeOrigin=...` from the URL, validate it against an allowlist of iframe hosts.
 *   2. If not authenticated in the Dashboard, embed the sign-in form (SignInForm) right in the
 *      popup. On a successful sign-in the auth state flips, initialState recomputes, and the flow
 *      advances to the token handshake automatically — no separate tab, no manual refresh.
 *   3. PostMessage the opener iframe to mint a fresh Shopify session token. The iframe responds
 *      with { token, shopDomain }.
 *   4. Render "Connect <FS Company Name> to <shopDomain>?" + Connect button.
 *   5. POST /integrations/shopify/admin-api/link with the user's FS JWT + Shopify session token.
 *   6. PostMessage the iframe with linked / failed, then window.close().
 *
 * Security note: the iframeOrigin validation in `isAllowedIframeOrigin` is the load-bearing line
 * — without it an attacker who could lure a signed-in Dashboard user to a crafted
 * `/connect-shopify?iframeOrigin=https://attacker.example` URL would be able to harvest a freshly
 * minted Shopify session token. The allowlist is strict in production (single iframe origin); dev
 * mode (localhost Dashboard) loosens it to any HTTPS origin so developers can wire up shopify-app-dev
 * Cloudflare tunnels without per-tunnel config changes.
 */

type ScreenState =
  | { kind: "loading_origin" }
  | { kind: "invalid_origin"; reason: string }
  | { kind: "no_opener" }
  | { kind: "needs_signin" }
  | { kind: "waiting_for_token" }
  | { kind: "token_error"; message: string }
  | { kind: "ready_to_link"; shopDomain: string | undefined }
  | { kind: "submitting"; shopDomain: string | undefined }
  | { kind: "linked"; companyName: string }
  | { kind: "failed"; code: string; message: string; canRetry: boolean };

function isAllowedIframeOrigin(origin: string): boolean {
  // Production iframe origin.
  if (origin === "https://sh.freightsimple.com") return true;
  if (origin === "https://shopify.freightsimpledemo.com") return true;
  // Dev: the Dashboard runs on localhost; accept any HTTPS origin (so a shopify-app-dev
  // Cloudflare tunnel works without per-tunnel allowlist edits). Production builds run from
  // dashboard.freightsimple.com, so this branch is no-op there.
  if (
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
  ) {
    return /^https:\/\/[a-z0-9.-]+/i.test(origin);
  }
  return false;
}

function ConnectShopifyScreen() {
  const { isAuthenticated, accessToken, user } = useAuthentication();

  const iframeOriginFromQuery = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("iframeOrigin");
  }, []);

  const initialState = useMemo<ScreenState>(() => {
    if (!iframeOriginFromQuery) {
      return {
        kind: "invalid_origin",
        reason: "Missing iframeOrigin query parameter.",
      };
    }
    if (!isAllowedIframeOrigin(iframeOriginFromQuery)) {
      return {
        kind: "invalid_origin",
        reason: `Iframe origin ${iframeOriginFromQuery} is not on the allowlist.`,
      };
    }
    if (!window.opener) {
      return { kind: "no_opener" };
    }
    if (!isAuthenticated) {
      return { kind: "needs_signin" };
    }
    return { kind: "waiting_for_token" };
  }, [iframeOriginFromQuery, isAuthenticated]);

  const [state, setState] = useState<ScreenState>(initialState);

  // Re-sync state when auth status changes (e.g. user signs in in another tab).
  useEffect(() => {
    setState(initialState);
  }, [initialState]);

  const iframeOrigin = iframeOriginFromQuery ?? "";

  // Ask the iframe which shop this is, so the confirmation screen can name it. The token that comes
  // back is deliberately discarded — a fresh one is minted when the merchant actually clicks, since
  // this screen then waits on a human and the token has about a minute to live.
  useEffect(() => {
    if (state.kind !== "waiting_for_token") return;
    let cancelled = false;
    void (async () => {
      try {
        const { shopDomain } = await requestShopifyToken(iframeOrigin);
        if (!cancelled) setState({ kind: "ready_to_link", shopDomain });
      } catch (e) {
        if (!cancelled) {
          setState({ kind: "token_error", message: (e as Error).message });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [state.kind, iframeOrigin]);

  /** Back to the top of the token handshake — what "Try again" does, in place of a reinstall. */
  const handleRetry = useCallback(() => {
    setState({ kind: "waiting_for_token" });
  }, []);

  const fail = useCallback(
    (code: string, backendMessage: string) => {
      const message = merchantMessage(code, backendMessage);
      window.opener?.postMessage(
        { type: "fs.connect.failed", code, message },
        iframeOrigin,
      );
      setState({ kind: "failed", code, message, canRetry: isRetryable(code) });
    },
    [iframeOrigin],
  );

  const handleConnect = useCallback(async () => {
    if (state.kind !== "ready_to_link") return;
    if (!accessToken) return;
    const shopDomain = state.shopDomain;
    setState({ kind: "submitting", shopDomain });

    // Minted here rather than when this screen appeared. The token is good for about a minute, and
    // between the button rendering and the merchant pressing it there is an unbounded human pause —
    // on the first live install that pause was long enough to expire it, and the only way past was
    // uninstalling and reinstalling the app (ENG-6681).
    let shopifySessionToken: string;
    try {
      shopifySessionToken = (await requestShopifyToken(iframeOrigin)).token;
    } catch (e) {
      fail("shopify_token_unavailable", (e as Error).message);
      return;
    }

    const api = new ShopifyAdminApi(apiServerUrl(), accessToken);
    // postLink answers 4xx and 5xx as results, but a network-level failure — offline, DNS, a dropped
    // connection — still throws. Unhandled, that leaves the merchant on the spinner forever with no
    // way out, which is the same dead end this ticket exists to remove.
    let result;
    try {
      result = await api.postLink({ shopifySessionToken });
    } catch (e) {
      fail("link_request_failed", (e as Error).message);
      return;
    }
    if (result.status === 200 && result.body.success) {
      const companyName =
        result.body.companyName ?? "your FreightSimple account";
      window.opener?.postMessage(
        { type: "fs.connect.linked", companyName },
        iframeOrigin,
      );
      setState({ kind: "linked", companyName });
      // Give the iframe a moment to receive the message before closing.
      window.setTimeout(() => window.close(), 750);
    } else {
      fail(
        result.body.error ?? `http_${result.status}`,
        result.body.message ??
          `Unexpected error linking this store (HTTP ${result.status}).`,
      );
    }
  }, [state, accessToken, iframeOrigin, fail]);

  return (
    <ShopifyConnectCard_ hideLogo={state.kind === "needs_signin"}>
      {renderBody(
        state,
        handleConnect,
        handleRetry,
        user?.name ?? user?.email ?? "your account",
      )}
    </ShopifyConnectCard_>
  );
}

/** A big, unmissable progress spinner + label for the flow's waiting states. */
function LoadingBody_({ label }: { label: string }): ReactElement {
  return (
    <Stack gap={4} align="center">
      <ConnectShopifySpinnerRow_>
        <ProgressSpinner size={ProgressSpinnerSizes.Medium} />
      </ConnectShopifySpinnerRow_>
      <T size="base" color="secondary">
        {label}
      </T>
    </Stack>
  );
}

/** Replica of the legacy Cargo ErrorMessage (18px medium red). */
function ConnectShopifyErrorText_({
  children,
}: {
  children: string;
}): ReactElement {
  return (
    <T size="lg" weight="medium" color="red">
      {children}
    </T>
  );
}

function renderBody(
  state: ScreenState,
  onConnect: () => void,
  onRetry: () => void,
  fsAccountLabel: string,
): ReactElement {
  switch (state.kind) {
    case "loading_origin":
      return <LoadingBody_ label="Loading…" />;
    case "invalid_origin":
      return (
        <Stack gap={3} align="center">
          <Heading level={1}>This page can't be opened directly</Heading>
          <T size="base" color="secondary">
            Open the FreightSimple app from inside your Shopify admin to start
            the connection flow. ({state.reason})
          </T>
        </Stack>
      );
    case "no_opener":
      return (
        <Stack gap={3} align="center">
          <Heading level={1}>
            This page must be opened from the Shopify app
          </Heading>
          <T size="base" color="secondary">
            Close this window and click "Connect" from the FreightSimple app in
            your Shopify admin.
          </T>
        </Stack>
      );
    case "needs_signin":
      // Sign in right here in the popup. On success the auth state flips, `initialState`
      // recomputes to `waiting_for_token`, and the flow advances automatically — no separate tab,
      // no manual refresh. Forgot-password / create-account are edge cases → open in a new tab.
      return (
        <AuthenticationSignIn
          redirectAfterSignin={false}
          onSuccess={() => {}}
          onForgotPassword={() =>
            window.open(
              "/request-reset-password",
              "_blank",
              "noopener,noreferrer",
            )
          }
          onCreateAccountInstead={() =>
            window.open("/create-account", "_blank", "noopener,noreferrer")
          }
        />
      );
    case "waiting_for_token":
      return <LoadingBody_ label="Connecting to your Shopify store…" />;
    case "token_error":
      return (
        <Stack gap={8} align="center">
          <Stack gap={3} align="center">
            <Heading level={1}>Couldn't reach the Shopify app</Heading>
            <ConnectShopifyErrorText_>{state.message}</ConnectShopifyErrorText_>
          </Stack>
          <Button primary size="large" onClick={onRetry}>
            Try again
          </Button>
        </Stack>
      );
    case "ready_to_link":
      return (
        <Stack gap={8} align="center">
          <Stack gap={3} align="center">
            <Heading level={1}>Connect to FreightSimple</Heading>
            <T size="base" color="secondary">
              Connect {fsAccountLabel}
              {state.shopDomain ? ` to ${state.shopDomain}` : ""}. FreightSimple
              will show live freight rates at checkout and handle your orders.
              You can disconnect any time by uninstalling the app in Shopify.
            </T>
          </Stack>
          <Button primary size="large" onClick={onConnect}>
            Connect
          </Button>
        </Stack>
      );
    case "submitting":
      return <LoadingBody_ label="Setting up FreightSimple in your store…" />;
    case "linked":
      return (
        <Stack gap={3} align="center">
          <Heading level={1}>Connected!</Heading>
          <T size="base" color="secondary">
            {state.companyName} is now linked to this Shopify store. You can
            close this window — the app refreshes automatically.
          </T>
        </Stack>
      );
    case "failed":
      return (
        <Stack gap={8} align="center">
          <Stack gap={2} align="center">
            <Heading level={1}>Connection failed</Heading>
            <ConnectShopifyErrorText_>{state.message}</ConnectShopifyErrorText_>
            <T size="base" color="secondary">
              Error code: {state.code}
            </T>
          </Stack>
          {state.canRetry ? (
            <Button primary size="large" onClick={onRetry}>
              Try again
            </Button>
          ) : (
            <T size="base" color="secondary">
              Contact FreightSimple support with the error code above.
            </T>
          )}
        </Stack>
      );
  }
}

export default ConnectShopifyScreen;
